import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import api, { type ApiResponse } from "../services/api";
import { registerAuthSessionInvalidationHandler } from "../services/authSessionInvalidation";
import { clearPublisherClientSession } from "../services/publisherClientSession";
import {
  readAuthTokenFromStorage,
  writeAuthTokenToStorage,
} from "../services/authSession";

export type AuthUser = {
  id: string;
  email: string;
  fullName: string;
  role: "founder" | "publisher";
};

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  setSession: (token: string, user: AuthUser) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(readAuthTokenFromStorage());
  const [isLoading, setIsLoading] = useState(true);

  const setSession = useCallback((nextToken: string, nextUser: AuthUser) => {
    writeAuthTokenToStorage(nextToken);
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const logout = useCallback(() => {
    clearPublisherClientSession();
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => registerAuthSessionInvalidationHandler(logout), [logout]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.post<
        ApiResponse<{ token: string; user: AuthUser }>
      >("/auth/login", { email, password });

      const nextUser = response.data.data.user;
      setSession(response.data.data.token, nextUser);
      return nextUser;
    },
    [setSession],
  );

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    api
      .get<ApiResponse<{ user: AuthUser }>>("/auth/me")
      .then((response) => {
        setUser(response.data.data.user);
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [token, logout]);

  const value = useMemo(
    () => ({ user, token, isLoading, login, logout, setSession }),
    [user, token, isLoading, login, logout, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider.");
  }
  return context;
}
